import { PIN_HEIGHT, PIN_WIDTH, PinInput, escapeHtml, cssUrl, renderTitle, requireImageUrl, brandInitials, PHOTO_GRADE } from "./types";

// venueCentered — moody, fully-centered editorial layout. The whole photo is
// dimmed under a soft vignette (darker at the corners, readable center) and
// the brand logo, headline, and an italic subtitle stack centered in the
// middle. No card, no eyebrow chip, no CTA pill — the cleanest of the three
// layouts. Best for atmospheric / destination-guide pins ("Things to do in
// {city} · While visiting {venue}").
export function venueCenteredTemplate(input: PinInput): string {
  const { brand, brandLogo, typography, venue } = input;
  const imageUrl = requireImageUrl(input, "venueCentered");
  if (!venue) {
    throw new Error("venueCentered template requires venue { name, location?, tagline? }");
  }

  // Centered text has the full width to work with; same ~2-line target.
  const nameLen = venue.name.length;
  const nameSize = nameLen <= 22 ? 100 : nameLen <= 32 ? 86 : nameLen <= 42 ? 74 : 64;

  // No-logo monogram: custom text or auto initials, serif or script.
  const monoText = (input.monogramText && input.monogramText.trim()) || brandInitials(brand);
  const monoClass = input.monogramFont === "serif" ? "serif" : "script";

  // Full-color logos (wax seals, crests) skip the monochrome whitening and
  // keep just the drop-shadow for legibility over the photo.
  const logoFilter = input.logoFullColor
    ? "drop-shadow(0 2px 10px rgba(0,0,0,.5))"
    : "brightness(0) invert(1) drop-shadow(0 2px 10px rgba(0,0,0,.5))";

  return `<!doctype html>
<html><head>
<meta charset="utf-8" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="${typography.googleFontsHref}" />
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap" />
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{width:${PIN_WIDTH}px;height:${PIN_HEIGHT}px;font-family:${typography.bodyFont};-webkit-font-smoothing:antialiased;background:#000}
  .pin{position:relative;width:100%;height:100%;overflow:hidden}
  .image{position:absolute;inset:0;background-image:url('${cssUrl(imageUrl)}');background-size:cover;background-position:${Math.round((input.focusX ?? 0.5) * 100)}% center;filter:${PHOTO_GRADE}}
  /* Full dim + soft vignette: readable in the center where the text sits,
     deeper at the corners for a moody, editorial feel. */
  .image::after{content:"";position:absolute;inset:0;background:radial-gradient(ellipse 92% 78% at 50% 46%,rgba(0,0,0,.46) 0%,rgba(0,0,0,.46) 52%,rgba(0,0,0,.64) 100%)}
  .overlay{position:absolute;inset:0;z-index:2;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:90px 72px;color:#fff}
  /* max-height (not fixed height) + max-width so wide wordmarks scale down
     proportionally instead of squishing. ~130px tall for normal logos. */
  .logo{max-height:200px;max-width:96%;width:auto;height:auto;display:block;margin-bottom:44px;filter:${logoFilter}}
  /* No-logo fallback monogram. Serif = ringed block mark; script = floating
     signature (no ring — a flourishy script never sits cleanly in a circle). */
  .monogram{margin-bottom:40px;color:#fff;display:flex;align-items:center;justify-content:center;text-shadow:0 2px 12px rgba(0,0,0,.5)}
  .monogram.serif{width:150px;height:150px;border:3px solid rgba(255,255,255,.85);border-radius:50%;font-family:${typography.headlineFont};font-size:62px;letter-spacing:3px}
  .monogram.script{font-family:'Great Vibes',cursive;font-size:108px;letter-spacing:1px;line-height:1}
  .name{font-family:${typography.headlineFont};font-size:${nameSize}px;font-weight:${typography.headlineWeight};line-height:1.12;letter-spacing:-1px;padding-bottom:.16em;text-shadow:0 2px 18px rgba(0,0,0,.5);display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
  .name em{font-style:italic;font-weight:400}
  .subtitle{font-family:${typography.headlineFont};font-style:italic;font-size:46px;line-height:1.3;opacity:.92;margin-top:26px;max-width:90%;padding-bottom:.16em;text-shadow:0 2px 14px rgba(0,0,0,.5);display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
</style></head>
<body><div class="pin">
  <div class="image"></div>
  <div class="overlay">
    ${
      input.hideLogo
        ? ""
        : brandLogo
          ? `<img class="logo" src="${brandLogo}" alt="${escapeHtml(brand)}" />`
          : `<div class="monogram ${monoClass}">${escapeHtml(monoText)}</div>`
    }
    <div class="name">${renderTitle(venue.name, input.titleAccent)}</div>
    ${venue.tagline ? `<div class="subtitle">${escapeHtml(venue.tagline)}</div>` : ""}
  </div>
</div></body></html>`;
}
